"use client";

import type { ReactNode } from "react";
import { useCallback, useEffect, useReducer, useRef, useState } from "react";
import { usePathname, useRouter } from "next/navigation";
import { DEFAULT_AUTH_CONTEXT, AuthContext, authReducer } from "./auth-context";
import { AuthWrapperWc } from "./auth-wrapper-wc";
import type {
  AuthWrapperWcProperties,
  WcAuthWrapperElement,
} from "./auth-wrapper-wc";
import type { Config } from "@jetblue/dotcom-config/dotcom";
import { handleLogin } from "@jetblue/dotcom-dynamic-yield";
import { handleLogout } from "./handle-logout";
import type { AuthWrapperConfig } from "../types";

const LOGOUT_PATH = "/logout";

export const AuthProvider = ({
  authConfig,
  children,
  fullStoryOrgId,
}: {
  authConfig: AuthWrapperConfig;
  children: ReactNode;
  fullStoryOrgId: Config["FULLSTORY_ORG_ID"];
}) => {
  const hasCalledLogout = useRef(false);
  const announcedLoginTbNumber = useRef<string | undefined>(undefined);
  const [isWCReady, setIsWCReady] = useState(false);
  const pathname = usePathname();
  const router = useRouter();
  const webComponentReference = useRef<WcAuthWrapperElement>(null);

  const [authContext, dispatch] = useReducer(authReducer, DEFAULT_AUTH_CONTEXT);

  const wcLogout = useCallback(() => {
    if (webComponentReference.current?.logout && !hasCalledLogout.current) {
      webComponentReference.current.logout();
      hasCalledLogout.current = true;
    }
  }, [hasCalledLogout]);

  useEffect(() => {
    if (pathname.includes(LOGOUT_PATH) && !hasCalledLogout.current) {
      wcLogout();
    }
  }, [hasCalledLogout, isWCReady, pathname, wcLogout]);

  const onReady = useCallback(() => {
    if (pathname.includes(LOGOUT_PATH) && !hasCalledLogout.current) {
      wcLogout();
    } else {
      if (
        !isWCReady &&
        typeof webComponentReference.current?.start === "function"
      ) {
        webComponentReference.current.start();
        setIsWCReady(true);
      }
    }
  }, [pathname, hasCalledLogout, isWCReady, wcLogout, webComponentReference]);

  const onActions = useCallback<AuthWrapperWcProperties["onActions"]>(
    (event) => {
      const { detail } = event;
      const shouldCallLogout =
        detail.action === "LOGOUT_STATUS" && detail.value === "Completed";

      if (shouldCallLogout) {
        announcedLoginTbNumber.current = undefined;
      }

      if (shouldCallLogout && pathname.includes(LOGOUT_PATH)) {
        handleLogout({ router });
      }

      dispatch(detail);
    },
    [pathname, router],
  );

  const onResources = useCallback<AuthWrapperWcProperties["onResources"]>(
    (event) => {
      const { detail } = event;

      if (detail.action === "PROFILE") {
        /* eslint-disable @typescript-eslint/no-unnecessary-condition -- runtime omits loyalty details */
        const tbNumber =
          detail.value.customerDetails.loyaltyDetails?.membershipID;
        /* eslint-enable @typescript-eslint/no-unnecessary-condition */
        if (tbNumber && tbNumber !== announcedLoginTbNumber.current) {
          announcedLoginTbNumber.current = tbNumber;
          void handleLogin(tbNumber);
        }
      }

      dispatch(detail);
    },
    [],
  );

  return (
    <>
      <AuthWrapperWc
        clientId={authConfig.CLIENT_ID}
        excludedRoutes={authConfig.EXCLUDED_ROUTES.join(",")}
        fullStoryOrgId={fullStoryOrgId}
        handleReturnUrl={authConfig.HANDLE_RETURN_URL}
        idleTimerDuration={authConfig.IDLE_TIMER_DURATION}
        isProduction={authConfig.IS_PRODUCTION ? "true" : "false"}
        issuer={authConfig.ISSUER}
        logoutWhenIdle={authConfig.LOGOUT_WHEN_IDLE}
        onActions={onActions}
        onReady={onReady}
        onResources={onResources}
        redirectUri={authConfig.REDIRECT_URI}
        ref={webComponentReference}
        scriptSource={authConfig.WEB_COMPONENT_SRC}
      />

      <AuthContext.Provider value={authContext}>
        {children}
      </AuthContext.Provider>
    </>
  );
};
